<template>
    <Layout
        :icon="props.options?.svgIcon"
        class="widget-page-error"
    >
        <div class="text-content">
            <EmptySpace />
            <PageHeader
                :title="props.options?.title || t('views.widget.error.title')"
                :description="props.options?.description || t('views.widget.error.description')"
            />

            <Position x="center">
                <ErrorText
                    v-if="props.options?.error"
                    :error="props.options?.error"
                />
            </Position>

            <TroubleshootingSection
                v-if="translatedTroubleshooting"
                v-bind="translatedTroubleshooting"
            />
        </div>

        <template #actions>
            <PlainActionsBar :actions="actions" />
        </template>
    </Layout>
</template>

<script lang="ts" setup>
import useEmbed from '@/embed/code'
import { useI18n } from '@/composables/i18n/useI18n'
import useWidgetHooks from '@/composables/widget-hooks/useWidgetHooks'
import type { ViewOptions } from '@/composables/view-manager/types'
import type { Action } from '@/components/ActionsBar.vue'

import ErrorText from 'widget-components/components/ErrorText/ErrorText.vue'
import Position from 'widget-components/components/Position/Position.vue'
import EmptySpace from 'widget-components/components/EmptySpace/EmptySpace.vue'
import PlainActionsBar from '@/components/PlainActionsBar.vue'
import Layout from '@/components/Layout.vue'
import PageHeader from '@/components/PageHeader.vue'
import TroubleshootingSection from '@/components/TroubleshootingSection.vue'
import { computed, onMounted } from 'vue'
import { captureMessage } from '@sentry/vue'
import useCloseWidgetButton from '@/composables/widget-close-button/useCloseWidgetButton'
import useWidgetOptions from '@/composables/widget-options/useWidgetOptions'
import type { TroubleshootingInfo } from '@/composables/websocket/useWebsocket'

const { reinitialize } = useWidgetHooks()

const { options: widgetOptions } = useWidgetOptions()

const { closeWidget } = useEmbed()

const { t, tm, rt } = useI18n()

const props = defineProps<{ options?: ViewOptions }>()

const troubleshooting = computed<TroubleshootingInfo | undefined>(() => {
    const error = props.options?.errorDetails

    if (error && typeof error === 'object' && 'troubleshooting' in error) {
        return error.troubleshooting as TroubleshootingInfo
    }

    return undefined
})

const translatedTroubleshooting = computed(() => {
    if (!troubleshooting.value) return undefined

    return {
        title: t(troubleshooting.value.title),
        intro: t(troubleshooting.value.intro),
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        steps: (tm(troubleshooting.value.steps) as any).map(rt),
    }
})

const again: Action = {
    label: t('common.button_again'),
    callback: () => {
        reinitialize()
    },
}

const close = computed<Action>(() => ({
    label: t('common.button_close'),
    secondary: props.options?.retry,
    callback: () => {
        closeWidget()
    },
}))

const forceShowClose = computed(() => {
    return !widgetOptions.value.inline &&
        props.options?.close
})

const actions = computed<Action[]>(() => {
    const { widgetCloseButtonAvailable }= useCloseWidgetButton()

    const _actions = []

    if (props.options?.retry) {
        _actions.push(again)
    }

    if (forceShowClose.value || widgetCloseButtonAvailable.value) {
        _actions.push(close.value)
    }

    return _actions
})

onMounted(() => {
    captureMessage(props.options?.error || 'WidgetErrorView', {
        level: 'warning',
        extra: {
            error: props.options?.errorDetails,
        },
    })
})
</script>

<style lang="sass" scoped>
.widget-page-error
    max-width: 420px
    align-self: center
</style>
